import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { Table, Label } from 'semantic-ui-react';

import { useAppDispatch } from '../../../hooks';
import Grid from '../../../components/common/grid';
import ContextMenu from '../../../components/common/grid/contextMenu';
import { gridKeys } from '../../../constants/gridKeys';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import { Common } from '../../../components';
import Panel from '../../../components/common/grid/managed/panel';
import { systemSettingsRoutes } from '../../systemSettings/constants';
import { WriteOnly } from '../../../components/common/grid/WriteOnly';
import { Record as RecordUtil } from '../../../modules/bin/utility';

import {
  clearCampaignOrganizerErrors,
  getCampaignOrganizerListData,
  getCampaignOrganizerListDataBySearchTerm,
  getCampaignOrganizerMetadata,
  deleteCampaignOrganizer,
} from '../../campaignOrganizers/store/campaignOrganizerActions';
import { campaignOrganizerColumns } from '../../../constants/campaignOrganizer';
import { ICampaignOrganizer } from '../../../models/CampaignOrganizer';
import DeleteCampaignOrganizerModal from './DeleteCampaignOrganizerModal';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

const getPanel = (onCreate: () => void) => (
  <Panel name='right'>
    <WriteOnly>
      <Common.Button primary onClick={onCreate}>
        <Translate value='campaignOrganizers.page.view.create-action' />
      </Common.Button>
    </WriteOnly>
  </Panel>
);

const CampaignOrganizersContent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const state = useSelector((s: RootState) => (s as any).campaignOrganizer);

  const [deleteTarget, setDeleteTarget] = useState<ICampaignOrganizer | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          getMetadata: getCampaignOrganizerMetadata,
          getListData: getCampaignOrganizerListData,
          clearRecordMessages: clearCampaignOrganizerErrors,
          getListDataBySearchTerm: getCampaignOrganizerListDataBySearchTerm,
        },
        dispatch
      ),
    [dispatch]
  );

  const openDelete = useCallback((item: ICampaignOrganizer) => {
    // Clear any lingering success/error from a previous delete so this
    // session's close-on-success effect doesn't fire immediately, and a
    // previous error message doesn't pre-fill the modal for a new target.
    dispatch(clearCampaignOrganizerErrors('recordDelete') as any);
    setDeleteError(null);
    setDeleteTarget(item);
  }, [dispatch]);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget?.id) return;
    // entityHandlers.deleteRecord dispatches the request; the reducer handles
    // success by invalidating the list (forces a refetch). NotFound surfaces
    // through the record's error.errorKeys and is shown by RecordResultMessage.
    dispatch(deleteCampaignOrganizer(deleteTarget.id) as any);
  }, [deleteTarget, dispatch]);

  // Close the modal once the delete record reports success (recordDelete.ready === 0 + success.show).
  React.useEffect(() => {
    const r = state?.recordDelete;
    if (deleteTarget && r?.ready === 0 && r?.success?.show) {
      closeDelete();
    } else if (deleteTarget && r?.error?.show) {
      const key = r?.error?.errorKeys?.[0];
      setDeleteError(
        key ? I18n.t(`error-messages.${key}`, { defaultValue: key }) : I18n.t('campaignOrganizers.page.delete.genericError')
      );
    }
  }, [state?.recordDelete, deleteTarget, closeDelete]);

  const contextMenu = (
    <ContextMenu>
      <ContextMenu.Item
        key='campaign-organizer-edit'
        label={I18n.t('campaignOrganizers.page.view.edit-action')}
        writeAction
        onClick={(_id: number, item: ICampaignOrganizer) => {
          if (item?.id) {
            navigate(`${systemSettingsRoutes.CAMPAIGN_ORGANIZERS_EDIT}/${item.id}`);
          }
        }}
      />
      <ContextMenu.Item
        key='campaign-organizer-delete'
        label={I18n.t('campaignOrganizers.page.view.delete-action')}
        writeAction
        isHiden={(item: ICampaignOrganizer) => item?.enabled === false}
        onClick={(_id: number, item: ICampaignOrganizer) => {
          if (item?.id) openDelete(item);
        }}
      />
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.CAMPAIGN_ORGANIZERS.CAMPAIGNS.key}
        label={I18n.t('campaignOrganizers.page.view.campaigns-action')}
        onClick={(_id: number, item: ICampaignOrganizer) => {
          if (item?.id) {
            navigate(CONTEXT_MENU_KEYS.CAMPAIGN_ORGANIZERS.CAMPAIGNS.getUrl({ id: item.id, name: item.name }, {}));
          }
        }}
      />
    </ContextMenu>
  );

  // Renders the name cell with a strikethrough + Deleted badge when the row is
  // soft-deleted. The marker activates once the API starts returning
  // `enabled` on element responses (false === deleted); until then `item.enabled`
  // is undefined, this check is falsy, and rows render normally.
  const columnRender = useCallback((col: any, colData: any, item: ICampaignOrganizer) => {
    if (col.id !== campaignOrganizerColumns.NAME) return null;
    if (item?.enabled === false) {
      return (
        <Table.Cell key={`${col.id}-deleted`}>
          <span style={{ textDecoration: 'line-through', color: 'var(--text-muted, #888)' }}>
            {colData}
          </span>
          <Label size='tiny' color='red' style={{ marginLeft: 8 }}>
            <Translate value='campaignOrganizers.page.view.deletedBadge' />
          </Label>
        </Table.Cell>
      );
    }
    return null;
  }, []);

  const panel = getPanel(() => navigate(systemSettingsRoutes.CAMPAIGN_ORGANIZERS_CREATE));
  const clearListMessages = () => dispatch(clearCampaignOrganizerErrors(gridKeys.CAMPAIGN_ORGANIZERS) as any);

  return (
    <>
      <RecordResultMessage
        record={state.record}
        onDismiss={() => dispatch(clearCampaignOrganizerErrors('record') as any)}
      />
      <h2>
        <Translate value='campaignOrganizers.page.view.title' />
      </h2>
      <RecordResultMessage
        record={state.campaignOrganizersList}
        onDismiss={clearListMessages}
      />
      <Grid.Managed
        listKey={gridKeys.CAMPAIGN_ORGANIZERS}
        list={state.campaignOrganizersList}
        actions={{ ...actions }}
        search
        filtersButton={false}
        columnRender={columnRender}
      >
        {panel}
        {contextMenu}
      </Grid.Managed>
      <DeleteCampaignOrganizerModal
        isOpen={!!deleteTarget}
        item={deleteTarget}
        isDeleting={RecordUtil.isRecordLoading(state?.recordDelete)}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
};

export default CampaignOrganizersContent;
